import {useState, useEffect} from "react";
import {Activity, MousePointer, Clock, ChevronRight, Play} from "lucide-react";

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
  </div>
);

const AdminDashboard = () => {
  const [userData, setUserData] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Basic ${process.env.REACT_APP_AWS_USER_TOKEN}`,
        };

        const [userRes, videoRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-contacts`, {
            method: "GET",
            headers,
          }),
          fetch(`${process.env.REACT_APP_AWS_USER_ENDPOINT}/get-videos`, {
            method: "GET",
            headers,
          }),
        ]);

        const userDataJson = await userRes.json();
        const videoDataJson = await videoRes.json();

        if (videoDataJson.result === "success") {
          setVideos(videoDataJson.data);
        }

        if (userDataJson.result === "success" && userDataJson.data.length > 0) {
          setUserData(userDataJson.data);
          setSelectedUser(userDataJson.data[0]);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (isoString) => new Date(isoString).toLocaleString();

  // New helper to format seconds into Hh Mm Ss
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${hours > 0 ? hours + "h " : ""}${minutes > 0 ? minutes + "m " : ""}${seconds}s`;
  };

  const getVideoInfo = (id) => {
    const video = videos.find((v) => v.id.toString() === id.toString());
    return video
      ? {title: video.title, category: video.category}
      : {title: `Video #${id}`, category: "Unknown Category"};
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <OrbitLoader />
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-gray-500 font-medium">No user data available.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full font-sans">
      {/* --- Sidebar Navigation --- */}
      <nav className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {userData.map((user) => (
            <button
              key={user.customer_id}
              onClick={() => setSelectedUser(user)}
              className={`w-full text-left p-2 rounded-xl transition-all flex items-center justify-between group ${
                selectedUser.customer_id === user.customer_id
                  ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                  : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              <div className="truncate pr-2">
                <p className="text-sm font-semibold truncate">
                  {user.customer_firstname && user.customer_lastname
                    ? `${user.customer_firstname} ${user.customer_lastname}`
                    : user.customer_id.split("@")[0]}
                </p>
                <p className="text-xs opacity-70 truncate">
                  {user.customer_id}
                </p>
              </div>
              <ChevronRight
                size={16}
                className={
                  selectedUser.customer_id === user.customer_id
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }
              />
            </button>
          ))}
        </div>
      </nav>

      {/* --- Main Content Area --- */}
      <main className="flex-1 p-10 overflow-y-auto">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
              Analytics Overview
            </h1>
            <p className="text-gray-500 mt-1">
              Viewing data for{" "}
              <span className="font-medium text-gray-800">
                {selectedUser.customer_id}
              </span>
            </p>
          </div>
        </header>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<MousePointer size={20} />}
            label="Total Clicks"
            value={selectedUser.activity?.clicks || 0}
            color="bg-orange-100 text-orange-600"
          />
          <StatCard
            icon={<Activity size={20} />}
            label="Mouse Moves"
            value={selectedUser.activity?.mouseMoves?.toLocaleString() || 0}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard
            icon={<Clock size={20} />}
            label="Active Time"
            value={formatTime(selectedUser.activity?.secondsActive || 0)}
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={<Play size={20} />}
            label="Video Playtime"
            value={formatTime(selectedUser.activity?.totalVideoSeconds || 0)}
            color="bg-purple-100 text-purple-600"
          />
        </div>

        {/* Watch History Bar Chart View */}
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mb-4">
          <div className="mb-8">
            <h2 className="text-xl font-bold">Watch History</h2>
            <p className="text-gray-400 text-sm">
              Detailed engagement per Video
            </p>
          </div>

          <div className="grid gap-6">
            {selectedUser.watch_history &&
            Object.entries(selectedUser.watch_history).length > 0 ? (
              Object.entries(selectedUser.watch_history).map(
                ([id, seconds]) => {
                  const videoInfo = getVideoInfo(id);
                  return (
                    <div
                      className="flex flex-col mb-2 rounded-lg border border-gray-200 p-4"
                      key={id}
                    >
                      <div className="flex justify-between items-end mb-2">
                        <div className="flex items-center gap-2 truncate">
                          <span className="text-sm font-bold text-gray-800 truncate">
                            {videoInfo.title}
                          </span>
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-100 px-2 py-0.5 rounded">
                            {videoInfo.category}
                          </span>
                        </div>
                        <span className="text-blue-600 text-sm font-bold">
                          {formatTime(seconds)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="bg-blue-500 h-full rounded-full transition-all duration-700 ease-out"
                          style={{
                            width: `${Math.min((seconds / (selectedUser.activity?.totalVideoSeconds || 1)) * 100, 100)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                },
              )
            ) : (
              <p className="text-gray-400 italic text-sm">
                No watch history recorded.
              </p>
            )}
          </div>
        </div>

        <div className="flex text-sm text-right bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 text-[12px]">
          <p className="font-bold text-gray-400 uppercase block">
            Last Recorded Activity:
          </p>
          <p className="ml-2 text-gray-400 block">
            {selectedUser.timestamp
              ? formatDate(selectedUser.timestamp)
              : "N/A"}
          </p>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({icon, label, value, color}) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-5 hover:shadow-md transition-shadow">
    <div className={`p-3 rounded-2xl ${color}`}>{icon}</div>
    <div>
      <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">
        {label}
      </p>
      <p className="text-2xl font-black">{value}</p>
    </div>
  </div>
);

export default AdminDashboard;
